/**
 * Conteúdo das perguntas frequentes.
 *
 * As perguntas são separadas por público porque as respostas sobre custo e pagamento são
 * opostas para cada lado: quem contrata não paga nada à plataforma, quem trabalha paga a
 * mensalidade. Uma lista única obrigaria o visitante a filtrar mentalmente o que é dele.
 *
 * Os `id` são slugs estáveis e viram âncora na URL
 * (ex.: /perguntas-frequentes#atraso-no-pagamento), então não devem ser renomeados sem
 * necessidade — links antigos deixariam de abrir a resposta certa.
 *
 * Este arquivo é a única fonte do texto: a página, o bloco de destaque da Home e o JSON-LD
 * enviado ao Google leem daqui, para que nunca divirjam entre si.
 *
 * Um trecho entre `**` vira sublinhado na tela (ItemPergunta faz o parse); no JSON-LD os `**`
 * são removidos, porque dados estruturados não aceitam marcação.
 */

import { textoDeVenda } from 'config/textosDeVenda';

export const GRUPOS = [
  {
    id: 'clientes',
    rotulo: 'Quero contratar',
    perguntas: [
      {
        id: 'quanto-custa-para-contratar',
        pergunta: 'Contratar pela Nova Faxina tem algum custo?',
        resposta: [
          'Não. Para quem contrata uma diarista ou faxineira, a plataforma é gratuita: não há taxa de cadastro, mensalidade nem comissão sobre o serviço. Você paga apenas o valor combinado com o profissional.',
          'O profissional começa grátis no plano Livre e pode passar para o Pro, por R$ 7,90 por mês.',
        ],
      },
      {
        // O mesmo fluxo aparece resumido nos cards "Como funciona" da Home
        // (PASSOS_AGENDAMENTO, em src/pages/Home/index.jsx), com texto próprio: lá o visitante ainda
        // não conhece as telas, aqui os nomes literais são o que orienta quem já está dentro.
        // Mudou o fluxo, mudam os dois.
        id: 'como-funciona-o-agendamento',
        pergunta: 'Como funciona o agendamento, do pedido até a confirmação?',
        resposta: ['São quatro passos, todos dentro da plataforma:'],
        itens: [
          'Escolha sua cidade e veja quais diaristas e profissionais de limpeza estão disponíveis, com o valor por pacote de horas ou por dia e a média de avaliações.',
          'Selecione o dia no calendário e o horário de entrada — a Limpeza Geral já vem incluída, e você pode pedir atenção especial em até 3 pontos, como as janelas, o armário ou a roupa.',
          'Envie o pedido. Ele fica como "Aguardando confirmação" até o profissional aceitar.',
          'Com a confirmação, o agendamento passa a "Confirmada" em Minha agenda e agora é aguardar o atendimento do profissional.',
        ],
        respostaFinal: [
          'Se preferir, você também pode abrir um pedido sem escolher um profissional específico e aguardar que alguém disponível na sua cidade aceite.',
        ],
      },
      {
        id: 'como-e-o-pagamento-do-servico',
        pergunta: 'Como e quando eu pago pelo serviço?',
        resposta: [
          'O pagamento é feito diretamente ao profissional, na forma que vocês combinarem. A Nova Faxina não retém nem processa o valor da faxina, então tudo o que você paga fica com quem executou o serviço.',
          'E o valor não é surpresa: o preço por pacote de horas ou por dia definido pelo profissional aparece na busca e na tela de agendamento, antes de você enviar o pedido.',
        ],
      },
      {
        id: 'como-sao-avaliados-os-profissionais',
        pergunta: 'Como sei que posso confiar no profissional?',
        resposta: [
          'Todas as notas vêm de clientes reais: a avaliação em estrelas só pode ser dada por quem teve um agendamento confirmado e já concluído com aquele profissional. Não existe avaliação de quem nunca contratou.',
          'A média aparece na busca e você pode ordenar os resultados por melhor avaliação. O cadastro é restrito a maiores de 18 anos e os Termos de Uso exigem informações verdadeiras — perfis com indícios de fraude podem ser suspensos ou encerrados.',
        ],
      },
      {
        id: 'posso-cancelar-um-agendamento',
        pergunta: 'Posso cancelar ou alterar um agendamento?',
        resposta: [
          'Pode. Enquanto a data do serviço não chegou, o botão Cancelar fica disponível no agendamento, dentro de Minha agenda.',
          'Pedimos que você informe o motivo, que fica registrado e visível para a outra parte — é o que mantém a relação transparente dos dois lados. Para mudar data, horário ou serviços, cancele o pedido atual e faça um novo com as informações corretas.',
        ],
      },
      {
        id: 'protecao-de-dados',
        pergunta: 'Meus dados pessoais ficam protegidos?',
        resposta: [
          'Sim. Tratamos dados pessoais conforme a LGPD e a nossa Política de Privacidade, usando-os apenas para viabilizar cadastro, busca, agendamento, segurança e suporte.',
          'Isso aparece em detalhes práticos: durante a busca os nomes são exibidos de forma parcial, e o nome completo do profissional só é revelado depois que ele confirma o agendamento.',
        ],
      },
    ],
  },
  {
    id: 'profissionais',
    rotulo: 'Quero trabalhar',
    perguntas: [
      {
        id: 'quanto-custa-para-trabalhar',
        pergunta: 'Quanto custa para trabalhar pela Nova Faxina?',
        resposta: [
          // Preço literal: este é um módulo de dados estático, e torná-lo dinâmico transformaria um
          // `def` em fetch. Reajuste no catálogo (/admin/financeiro/planos) exige passar pela
          // resposta do grupo de clientes que cita o Pro, por config/textosDeVenda e pelo cartão da Home.
          // As duas frases do plano vêm de textosDeVenda (versão completa desde a C5d: o Pro vem antes do
          // Livre só na ordem padrão da busca, e o texto diz isso).
          `${textoDeVenda('faqQuantoCusta')} **Não cobramos comissão sobre os serviços realizados: o que você combina com o cliente é integralmente seu.**`,
          textoDeVenda('faqDiferencaDosPlanos'),
        ],
      },
      {
        id: 'selo-fundador',
        pergunta: 'O que é o selo Fundador(a)?',
        resposta: [
          'É o selo das primeiras profissionais de cada cidade a ter o documento aprovado. Ele aparece no seu perfil e no seu card na busca, e é para sempre, mesmo no plano Livre.',
          'Quem recebe o selo ganha um período de Pro grátis, sem assinatura e sem cobrança, e depois um desconto no Pro mensal e no anual enquanto mantiver a assinatura. Se você já assinava, a próxima cobrança é adiada pelo mesmo período, já com o desconto. O desconto é perdido se você ficar um tempo sem assinatura depois de ter assinado com o selo; o selo continua.',
          'Você é avisado ao receber o selo, 15 dias antes e no último dia do Pro grátis. O período e o desconto aparecem no card “Meu plano” do seu painel e em Meu cadastro › Assinatura.',
        ],
      },
      {
        id: 'como-recebo-pelos-servicos',
        pergunta: 'Como e quando eu recebo pelos serviços?',
        resposta: [
          'Direto do cliente, sem intermediação da plataforma e sem prazo de repasse.',
          'Você define se cobra por pacote de horas ou por dia, e qual o valor. Essa informação aparece para o cliente antes de ele enviar o pedido, o que evita negociação desconfortável no dia do serviço.',
        ],
      },
      {
        id: 'formas-de-pagamento-assinatura',
        pergunta: 'Como pago a assinatura e posso cancelar quando quiser?',
        resposta: [
          'Você escolhe entre Pix e cartão de crédito. No Pix é gerado um QR Code novo a cada mês, pago manualmente; no cartão, a renovação é automática. Os dados do cartão são informados no ambiente seguro da Asaas, nosso processador de pagamentos, e não passam pelos servidores da Nova Faxina.',
          'Quando o cadastro oferece o Pro anual, ele é pago uma vez por ano, à vista, no Pix ou no cartão. Quem está em dia pode trocar entre o mensal e o anual em Meu cadastro › Assinatura: a troca vale a partir do próximo vencimento, sem cobrar nada na hora.',
          'Pode cancelar quando quiser, sem fidelidade nem multa. Em Meu cadastro › Assinatura, toque em "Cancelar a renovação do Pro": nada mais é cobrado, o Pro continua até o fim do período já pago e depois você volta ao plano Livre, sem sair das buscas. Se a assinatura estiver em atraso, o cancelamento é na hora e as cobranças em aberto são retiradas, sem dívida.',
          'Se você se arrependeu da assinatura, pode pedir a devolução do primeiro pagamento em até 7 dias pelo Fale conosco, no plano mensal e no anual. Para sair das buscas de vez, o caminho é outro: "Desativar meu perfil profissional", em Meu cadastro › Perfil Profissional.',
        ],
      },
      {
        id: 'atraso-no-pagamento',
        pergunta: 'O que acontece se eu atrasar a mensalidade?',
        resposta: [
          'Nada muda de imediato. Depois do vencimento existe um período de tolerância em que você continua no Pro, e a própria tela da assinatura mostra quantos dias ainda restam.',
          'Passado esse prazo sem pagamento, você volta para o plano Livre e continua aparecendo nas buscas. Pagando, volta ao Pro, com o cadastro, a agenda e o histórico de avaliações intactos.',
        ],
      },
      {
        id: 'como-recebo-pedidos',
        pergunta: 'Como recebo os pedidos e quem controla a minha agenda?',
        resposta: [
          'Com o documento aprovado, seu perfil aparece para os clientes da sua cidade, no Livre ou no Pro. Quando alguém solicita um atendimento você recebe uma notificação, e o pedido entra em Minha agenda como "Aguardando sua confirmação".',
          'Nenhum serviço é atribuído automaticamente: você aceita apenas o que couber na sua rotina. Os dias já comprometidos ficam bloqueados no calendário para novos pedidos, e antes de confirmar você vê o bairro, a cidade, os serviços solicitados e se o cliente oferece refeição.',
          'A confirmação vale até as 23:59 do dia anterior ao serviço. Pedido sem resposta nesse prazo é cancelado automaticamente, e o cliente é avisado para procurar outro profissional.',
        ],
      },
      {
        id: 'perfil-fora-da-busca-e-taxa-de-resposta',
        pergunta: 'Meu perfil pode sair da busca? O que é a taxa de resposta?',
        resposta: [
          'Se você ficar muito tempo sem abrir o app ou o site e sem responder pedidos, seu perfil sai da busca para os clientes não pedirem a quem não está atendendo. Você recebe um aviso antes, no app e por e-mail. Para voltar, basta abrir o app ou o site: o perfil volta na hora. Perfil pausado não entra nessa conta.',
          'A taxa de resposta mostra quantos pedidos você respondeu (confirmando ou cancelando) antes do prazo, entre os que chegaram nos últimos meses. Ela aparece no card “Meu plano” do seu painel. Com a taxa baixa, seu perfil aparece depois dos outros do seu plano na ordem padrão da busca, mas continua visível. O cliente não vê a sua taxa.',
        ],
      },
      {
        id: 'vinculo-empregaticio',
        pergunta: 'Existe vínculo empregatício com a Nova Faxina?',
        resposta: [
          'Não. A Nova Faxina é uma intermediadora tecnológica que aproxima clientes e profissionais, sem vínculo empregatício, de subordinação, exclusividade ou sociedade, como está descrito nos Termos de Uso.',
          'Você trabalha de forma autônoma: define os próprios preços, os dias em que quer atender e quais pedidos aceita.',
        ],
      },
    ],
  },
];

export const TODAS_PERGUNTAS = GRUPOS.flatMap((grupo) => grupo.perguntas);

/** Busca perguntas por id, preservando a ordem pedida. Usado no bloco de destaque da Home. */
export const pegarPerguntas = (...ids) =>
  ids.map((id) => TODAS_PERGUNTAS.find((item) => item.id === id)).filter(Boolean);

export const grupoDaPergunta = (id) =>
  GRUPOS.find((grupo) => grupo.perguntas.some((item) => item.id === id));

/** Resposta como texto corrido — o JSON-LD não aceita marcação, então o `**sublinhado**` sai. */
const respostaEmTexto = (item) =>
  [...(item.resposta || []), ...(item.itens || []), ...(item.respostaFinal || [])]
    .join(' ')
    .replace(/\*\*/g, '');

/**
 * Dados estruturados FAQPage. Não geram mais o resultado enriquecido de FAQ (desde 2023 o Google
 * o restringe a sites de governo e saúde), mas continuam válidos para o buscador ler a página.
 * Gerado a partir do mesmo conteúdo exibido na tela para os dois nunca divergirem.
 */
export const JSON_LD_FAQ = JSON.stringify({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: TODAS_PERGUNTAS.map((item) => ({
    '@type': 'Question',
    name: item.pergunta,
    acceptedAnswer: { '@type': 'Answer', text: respostaEmTexto(item) },
  })),
});
